import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Plus, User, Building2, Package, Calendar, Eye, Table, Grid, CheckCircle2, Trash2, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import LoadingOverlay from '@/components/LoadingOverlay';
import ModalRecoleccion from './ModalRecoleccion';
import TablaRecolecciones from './TablaRecolecciones';
import ModalVerificarRecoleccion from './ModalVerificarRecoleccion';
import ModalDetalleRecoleccion from './ModalDetalleRecoleccion';
import { getRecolecciones, createRecoleccion, updateRecoleccion, deleteRecoleccion } from '../services/deposito.services';
import { printThermalReceipt } from '@/utils/thermalPrinter';

const RecoleccionPrendas = ({ idPlanta, plantaNombre }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [recolecciones, setRecolecciones] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'cards' o 'table' - Por defecto tabla
  const [isVerificarDialogOpen, setIsVerificarDialogOpen] = useState(false);
  const [itemToVerificar, setItemToVerificar] = useState(null);
  const [isDetalleDialogOpen, setIsDetalleDialogOpen] = useState(false);
  const [itemToView, setItemToView] = useState(null);
  const [currentFilters, setCurrentFilters] = useState({ estado: 'recibido' });

  // Paginación para vista de cards
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;

  // Contador de pendientes para el badge
  const [pendientesCount, setPendientesCount] = useState(0);

  // Cargar recolecciones desde el backend con filtros
  const loadRecolecciones = useCallback(async (filters = currentFilters) => {
    if (!idPlanta) return;

    setLoading(true);
    try {
      const response = await getRecolecciones({
        idPlanta,
        ...filters
      });
      setRecolecciones(response?.data || []);

      // Si estamos en vista de tabla, cargar contador de pendientes para el badge
      if (viewMode === 'table') {
        const pendientesResponse = await getRecolecciones({
          idPlanta,
          estado: 'pendiente_verificacion'
        });
        setPendientesCount(pendientesResponse?.data?.length || 0);
      }
    } catch (error) {
      console.error('Error cargando recolecciones:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las recolecciones',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [idPlanta, currentFilters, toast, viewMode]);

  useEffect(() => {
    loadRecolecciones(currentFilters);
  }, [idPlanta, currentFilters]);

  // Manejar cambio de filtros desde TablaRecolecciones
  const handleFiltersChange = useCallback((newFilters) => {
    setCurrentFilters(newFilters);
  }, []);

  const handleSaveRecoleccion = async (recoleccionData) => {
    try {
      const response = await createRecoleccion(recoleccionData);

      toast({
        title: 'Éxito',
        description: 'Recolección creada correctamente',
      });

      // Recargar lista
      await loadRecolecciones();

      // Retornar la recolección creada para que el modal pueda imprimir
      return response;

    } catch (error) {
      console.error('Error guardando recolección:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar la recolección',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'recibido':
        return <Badge variant="outline" className="text-blue-600 border-blue-600">Recibido</Badge>;
      case 'pendiente_verificacion':
        return <Badge variant="outline" className="text-yellow-600 border-yellow-600">Pendiente</Badge>;
      case 'verificado':
        return <Badge variant="outline" className="text-green-600 border-green-600">Verificado</Badge>;
      case 'rechazado':
        return <Badge variant="outline" className="text-red-600 border-red-600">Rechazado</Badge>;
      case 'entregado':
        return <Badge variant="outline" className="text-purple-600 border-purple-600">Entregado</Badge>;
      default:
        return <Badge variant="outline">Desconocido</Badge>;
    }
  };

  // Funciones para manejar acciones de la tabla
  const handleEditRecoleccion = (recoleccion) => {
    toast({
      title: "Editar Recolección",
      description: `Función de edición para ${recoleccion.usuario.nombre}`,
    });
  };

  const handleDeleteRecoleccion = async (recoleccion) => {
    const id = recoleccion._originalData?._id
      || recoleccion._originalData?.id
      || recoleccion._id
      || recoleccion.id;

    await deleteRecoleccion(id);
    await loadRecolecciones(currentFilters);
  };

  const handleViewRecoleccion = (recoleccion) => {
    toast({
      title: "Ver Recolección",
      description: `Visualizando recolección de ${recoleccion.empleado?.nombre}`,
    });
  };

  const handlePrintRecoleccion = (recoleccion) => {
    try {
      if (!recoleccion) {
        throw new Error('No se recibieron datos de la recolección');
      }

      printThermalReceipt(recoleccion);
      toast({
        title: "Imprimiendo",
        description: "Enviando recibo a la impresora térmica...",
      });
    } catch (error) {
      console.error('Error al imprimir:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo imprimir el recibo",
        variant: "destructive",
      });
    }
  };

  const handleVerificarRecoleccion = (recoleccion) => {
    setItemToVerificar(recoleccion);
    setIsVerificarDialogOpen(true);
  };

  const handleVerDetalle = (recoleccion) => {
    setItemToView(recoleccion);
    setIsDetalleDialogOpen(true);
  };

  // Paginación para cards (en vista cards ya cargamos solo pendientes)
  const paginatedCards = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return recolecciones.slice(startIndex, endIndex);
  }, [recolecciones, currentPage]);

  const totalPages = Math.ceil(recolecciones.length / ITEMS_PER_PAGE);

  // Reset página y cargar datos apropiados al cambiar de vista
  useEffect(() => {
    setCurrentPage(1);

    // Si cambiamos a vista de cards, cargar pendientes de verificación
    if (viewMode === 'cards') {
      loadRecolecciones({ estado: 'pendiente_verificacion' });
    } else {
      // Si cambiamos a tabla, usar los filtros actuales
      loadRecolecciones(currentFilters);
    }
  }, [viewMode]);

  const handleConfirmarVerificacion = async (recoleccionActualizada) => {
    try {
      await updateRecoleccion(recoleccionActualizada._id || recoleccionActualizada.id, recoleccionActualizada);

      toast({
        title: 'Éxito',
        description: 'Recolección verificada correctamente',
      });

      // Recargar lista según la vista actual
      if (viewMode === 'cards') {
        // Si estamos en vista de cards, recargar pendientes
        await loadRecolecciones({ estado: 'pendiente_verificacion' });
      } else {
        // Si estamos en tabla, usar filtros actuales
        await loadRecolecciones(currentFilters);
      }

      // Cerrar modal
      setIsVerificarDialogOpen(false);
      setItemToVerificar(null);

    } catch (error) {
      console.error('Error verificando recolección:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo verificar la recolección',
        variant: 'destructive',
      });
      throw error;
    }
  };

  return (
    <div className="space-y-6">
      {/* Loading Overlay Global */}
      <LoadingOverlay isLoading={loading} message="Cargando recolecciones..." />

      {/* Header con botón para nueva recolección */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-500" />
                Recepción de Prendas
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Gestiona las recepciones de prendas de los usuarios
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Botones de vista */}
              <div className="flex items-center border rounded-lg">
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className="rounded-r-none relative"
                >
                  <Grid className="h-4 w-4" />
                  {viewMode === 'table' && pendientesCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#dc2626] text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {pendientesCount}
                    </span>
                  )}
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  className="rounded-l-none"
                >
                  <Table className="h-4 w-4" />
                </Button>
              </div>
              <Button onClick={() => setShowModal(true)} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Nueva Recolección
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Contenido condicional según el modo de vista */}
      {viewMode === 'table' ? (
        <TablaRecolecciones
          recolecciones={recolecciones}
          onEdit={handleEditRecoleccion}
          onDelete={handleDeleteRecoleccion}
          onView={handleViewRecoleccion}
          onVerificar={handleConfirmarVerificacion}
          onPrint={handlePrintRecoleccion}
          idPlanta={idPlanta}
          onFiltersChange={handleFiltersChange}
          loading={loading}
        />
      ) : (
        /* Vista de Cards - Solo Pendientes de Verificación */
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-yellow-500" />
              Pendientes de Verificación ({recolecciones.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {paginatedCards.length > 0 ? (
                <>
                  {paginatedCards.map((recoleccion) => (
                    <motion.div
                      key={recoleccion._id || recoleccion.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border rounded-lg p-6 bg-white dark:bg-gray-800"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <div className="bg-blue-500/10 p-3 rounded-lg">
                            <User className="h-6 w-6 text-blue-500" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{recoleccion.empleado?.nombre}</h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span>Cédula: {recoleccion.empleado?.cedula}</span>
                              <span>Tel: {recoleccion.empleado?.celular}</span>
                              <span className="flex items-center gap-1">
                                <Building2 className="h-4 w-4" />
                                {recoleccion.empleado?.area?.nombre || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className="flex items-center gap-2">
                            {getEstadoBadge(recoleccion.estado)}
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(recoleccion.createdAt).toLocaleString()}
                            </span>
                          </div>
                          {/* Botones de acción */}
                          <div className="flex items-center gap-2">
                            {recoleccion.estado === 'pendiente_verificacion' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleVerificarRecoleccion(recoleccion)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                title="Verificar recolección"
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleVerDetalle(recoleccion)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Ver detalle"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteRecoleccion(recoleccion)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Total de Prendas */}
                      <div className="space-y-3">
                        <h4 className="font-medium flex items-center gap-2">
                          <Package className="h-4 w-4" />
                          Total de Prendas: {recoleccion.cantidad_prendas || 0}
                        </h4>

                      </div>
                    </motion.div>
                  ))}

                  {/* Paginador */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-6">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Anterior
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                          <Button
                            key={page}
                            variant={currentPage === page ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className="w-10"
                          >
                            {page}
                          </Button>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Siguiente
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    No hay recolecciones pendientes de verificación
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Todas las recolecciones han sido procesadas
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal de Nueva Recolección */}
      <ModalRecoleccion
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSaveRecoleccion}
        idPlanta={idPlanta}
        plantaNombre={plantaNombre}
      />

      {/* Modal de Detalle */}
      <ModalDetalleRecoleccion
        isOpen={isDetalleDialogOpen}
        onClose={() => {
          setIsDetalleDialogOpen(false);
          setItemToView(null);
        }}
        recoleccion={itemToView}
      />

      {/* Modal de Verificación */}
      <ModalVerificarRecoleccion
        isOpen={isVerificarDialogOpen}
        onClose={() => {
          setIsVerificarDialogOpen(false);
          setItemToVerificar(null);
        }}
        recoleccion={itemToVerificar}
        onVerificar={handleConfirmarVerificacion}
      />
    </div>
  );
};

export default RecoleccionPrendas;
